import { ref, shallowRef, type Ref, type ShallowRef } from 'vue';
import { ApiError } from '../api';

export interface Resource<T> {
  data: ShallowRef<T | null>;
  error: Ref<string>;
  loading: Ref<boolean>;
  /** Recharge ; `quiet` garde les données affichées pendant le chargement (pas de squelette). */
  load: (quiet?: boolean) => Promise<void>;
}

export function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Une erreur inattendue est survenue.';
}

/**
 * Données d'une carte : chargement, erreur lisible, rechargement. Une réponse arrivée après une plus
 * récente (changement d'année rapide) est ignorée.
 */
export function useResource<T>(fetcher: () => Promise<T>): Resource<T> {
  const data = shallowRef<T | null>(null);
  const error = ref('');
  const loading = ref(false);
  let ticket = 0;

  async function load(quiet = false): Promise<void> {
    const mine = ++ticket;
    if (!quiet) loading.value = true;
    try {
      const value = await fetcher();
      if (mine !== ticket) return;
      data.value = value;
      error.value = '';
    } catch (err) {
      if (mine !== ticket) return;
      error.value = errorText(err);
      if (!quiet) data.value = null;
    } finally {
      if (mine === ticket) loading.value = false;
    }
  }

  return { data, error, loading, load };
}
